import React, { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
  type Placement,
} from '@floating-ui/react';
import { FiInfo } from 'react-icons/fi';
import { cn } from '../../utils/cn';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';

const LONG_PRESS_MS = 450;

export type TooltipTouchMode = 'tap' | 'longpress' | 'off';

export interface TooltipProps {
  content: React.ReactNode;
  /** A single element that can hold a ref. Wrap disabled buttons in a `<span>`. */
  children: React.ReactElement;
  placement?: Placement;
  /** Hover delay before opening, in ms. */
  delay?: number;
  /**
   * How touch screens open the tooltip. `tap` suits elements whose only job is
   * to explain (InfoTip); `longpress` suits buttons and links so a tap still acts.
   */
  touch?: TooltipTouchMode;
  disabled?: boolean;
  maxWidth?: number;
  className?: string;
}

type AnyProps = Record<string, unknown> & { ref?: React.Ref<HTMLElement> };

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  delay = 250,
  touch = 'longpress',
  disabled = false,
  maxWidth = 260,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [arrowEl, setArrowEl] = useState<SVGSVGElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const pressTimer = useRef<number | null>(null);
  const suppressClick = useRef(false);

  const enabled = !disabled && content !== null && content !== undefined && content !== '';

  const { refs, floatingStyles, context } = useFloating({
    open: enabled && open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      arrow({ element: arrowEl }),
    ],
  });

  const hover = useHover(context, { enabled, delay: { open: delay, close: 80 }, mouseOnly: true });
  const focus = useFocus(context, { enabled, visibleOnly: true });
  const click = useClick(context, { enabled: enabled && touch === 'tap', ignoreMouse: true });
  const dismiss = useDismiss(context, { enabled });
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : { open: 140, close: 90 },
    initial: { opacity: 0, transform: 'scale(0.96)' },
  });

  useEffect(
    () => () => {
      if (pressTimer.current) window.clearTimeout(pressTimer.current);
    },
    [],
  );

  const { ref: childRef, ...childProps }: AnyProps = isValidElement(children)
    ? (children.props as AnyProps)
    : {};
  const mergedRef = useMergeRefs([refs.setReference, childRef]);

  if (!isValidElement(children)) return children;

  const referenceProps = getReferenceProps(childProps) as Record<string, unknown>;
  const call = (name: string, event: React.SyntheticEvent) => {
    const handler = referenceProps[name];
    if (typeof handler === 'function') handler(event);
  };

  const longPressProps =
    enabled && touch === 'longpress'
      ? {
          onPointerDown: (e: React.PointerEvent) => {
            call('onPointerDown', e);
            if (e.pointerType !== 'touch') return;
            suppressClick.current = false;
            pressTimer.current = window.setTimeout(() => {
              suppressClick.current = true;
              setOpen(true);
            }, LONG_PRESS_MS);
          },
          onPointerUp: (e: React.PointerEvent) => {
            call('onPointerUp', e);
            if (pressTimer.current) window.clearTimeout(pressTimer.current);
          },
          onPointerLeave: (e: React.PointerEvent) => {
            call('onPointerLeave', e);
            if (pressTimer.current) window.clearTimeout(pressTimer.current);
          },
          onPointerCancel: (e: React.PointerEvent) => {
            call('onPointerCancel', e);
            if (pressTimer.current) window.clearTimeout(pressTimer.current);
          },
          onClickCapture: (e: React.MouseEvent) => {
            if (suppressClick.current) {
              suppressClick.current = false;
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            call('onClickCapture', e);
          },
          onContextMenu: (e: React.MouseEvent) => {
            if (suppressClick.current) e.preventDefault();
            call('onContextMenu', e);
          },
        }
      : {};

  // mergedRef is a callback ref; cloneElement only attaches it, nothing reads it during render.
  // eslint-disable-next-line react-hooks/refs
  const reference = cloneElement(children, {
    ...referenceProps,
    ...longPressProps,
    ref: mergedRef,
  } as AnyProps);

  return (
    <>
      {reference}
      {enabled && isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1000 }}
            {...getFloatingProps()}
          >
            <div
              style={{ ...styles, maxWidth }}
              className={cn(
                'rounded-xl border border-white/10 bg-[#161618] px-3 py-2 text-left text-[12.5px] font-normal leading-snug text-zinc-200 shadow-[0_12px_32px_rgba(0,0,0,0.55)]',
                className,
              )}
            >
              {content}
              <FloatingArrow
                ref={setArrowEl}
                context={context}
                fill="#161618"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth={1}
                width={12}
                height={6}
              />
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

/**
 * Explains a status badge. Focusable and tap-to-open; clicks don't reach a
 * clickable parent row, so tapping the badge never navigates away.
 */
export const StatusTip: React.FC<{
  content?: React.ReactNode;
  children: React.ReactNode;
  placement?: Placement;
  className?: string;
}> = ({ content, children, placement = 'top', className }) => {
  if (!content) return <>{children}</>;
  return (
    <Tooltip content={content} placement={placement} touch="tap" delay={200}>
      <span
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex cursor-help rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
          className,
        )}
      >
        {children}
      </span>
    </Tooltip>
  );
};

export interface InfoTipProps {
  content: React.ReactNode;
  /** Accessible name, e.g. "What is a deposit?". */
  label?: string;
  placement?: Placement;
  size?: number;
  maxWidth?: number;
  className?: string;
}

/** A small "i" button that explains the thing next to it. */
export const InfoTip: React.FC<InfoTipProps> = ({
  content,
  label = 'More info',
  placement = 'top',
  size = 14,
  maxWidth,
  className,
}) => (
  <Tooltip content={content} placement={placement} touch="tap" delay={120} maxWidth={maxWidth}>
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        // Keep the click from focusing an associated input or submitting a form.
        e.preventDefault();
        e.stopPropagation();
      }}
      className={cn(
        'inline-flex shrink-0 cursor-help items-center justify-center rounded-full text-zinc-500 transition-colors hover:text-zinc-200 focus-visible:text-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
        className,
      )}
    >
      <FiInfo size={size} aria-hidden />
    </button>
  </Tooltip>
);
